import * as React from 'react'

const teks = () => [
  {
    question: 'What is Quill?',
    answer: (
      <p>Quill.org is a non-profit dedicated to helping all students become strong writers, readers, and critical thinkers. Quill&#39;s six free literacy tools —
        <a className="focus-on-light" href="/tools/diagnostic" rel="noopener noreferrer" target="_blank">Quill Diagnostics</a>, <a className="focus-on-light" href="/tools/connect" rel="noopener noreferrer" target="_blank">Quill Connect</a>, <a className="focus-on-light" href="/tools/grammar" rel="noopener noreferrer" target="_blank">Quill Grammar</a>, <a className="focus-on-light" href="/tools/proofreader" rel="noopener noreferrer" target="_blank">
          Quill Proofreader</a>, <a className="focus-on-light" href="/tools/lessons" rel="noopener noreferrer" target="_blank">Quill Lessons</a>, and <a className="focus-on-light" href="/tools/evidence" rel="noopener noreferrer" target="_blank">
          Quill Reading for Evidence</a> — deliver immediate feedback and coaching to strengthen student writing and save teachers&apos; time. Through Quill&apos;s library of 1,000+ differentiated activities,
        students in grades 4-12 <b>learn to write</b> by mastering sentence construction and <b>write to learn</b> by reading cross-curricular texts and responding to open-ended prompts.
        As of 2026, over <b>12 million students</b> have strengthened their literacy skills with Quill, writing and revising over <b>three billion sentences</b>.</p>
    ),
    id: 'what-is-quill'
  },
  {
    question: 'How does Quill work?',
    answer: (
      <div>
        <p>The core of Quill is its feedback. Here&apos;s how it works:</p>
        <br />
        <p><strong>Real-time analysis of student writing</strong> - As soon as a student submits a sentence, Quill&apos;s AI checks it for specific skills
          (like subject-verb agreement, sentence structure, or use of evidence).</p>
        <br />
        <p><strong>Specific, skill-focused feedback</strong> - Instead of a generic “correct” or “incorrect,” Quill points out the exact issue
          (e.g., <em>"This sentence has a subject-verb agreement error"</em>) and often gives a short example or hint.</p>
        <br />
        <p><strong>Multiple revision opportunities</strong> - Students can edit and resubmit their work based on the feedback, reinforcing the skill through immediate practice.</p>
        <br />
        <p><strong>Adaptive learning paths</strong> - After a diagnostic or activity, Quill recommends follow-up activities targeted to
          each student&apos;s needs, ensuring they spend time on the skills they haven&apos;t yet mastered.</p>
        <br />
        <p><strong>Supports different ability levels</strong> - Feedback is written in clear, student-friendly language, and in many
          cases is available in both English and the student's chosen language for better accessibility.</p>
      </div>
    ),
    id: 'how-does-quill-work'
  },
  {
    question: "What are Quill's tools?",
    answer: (
      <div>
        <p>Quill's 1,000+ literacy activities across its <strong>six tools</strong> engage students in learning
          <em> to write</em> and <em>writing to learn</em>.</p>
        <br />
        <p>These tools include:</p>
        <p>- <a className='focus-on-light' href="/tools/diagnostic" rel="noopener noreferrer" target="_blank">Quill Diagnostics</a> to assess student
          writing and measure growth</p>
        <p>- <a className='focus-on-light' href="/tools/connect" rel="noopener noreferrer" target="_blank">Quill Connect</a> to combine sentences using
          conjunctions like but, because, so</p>
        <p>- <a className='focus-on-light' href="/tools/grammar" rel="noopener noreferrer" target="_blank">Quill Grammar</a> to practice grammar and
          mechanics</p>
        <p>- <a className='focus-on-light' href="/tools/proofreader" rel="noopener noreferrer" target="_blank">Quill Proofreader</a> to edit passages</p>
        <p>- <a className='focus-on-light' href="/tools/lessons" rel="noopener noreferrer" target="_blank">Quill Lessons</a> to lead direct instruction</p>
        <p>- <a className='focus-on-light' href="/tools/evidence" rel="noopener noreferrer" target="_blank">Quill Reading for Evidence</a> to analyze
          texts, identify claims, and support ideas with evidence.</p>
      </div>
    ),
    id: 'what-are-quills-tools'
  },
  {
    question: 'What Texas schools and districts are using Quill?',
    answer: (
      <p>Over 6,100 schools and 34,600 teachers use Quill in Texas to drive literacy growth and align to TEKS standards. Texas districts using Quill include Alief ISD, Bryan ISD, Humble ISD, ILTexas, Katy ISD, and Temple ISD.</p>
    ),
    id: 'texas-schools-and-districs-using-quill'
  },
  {
    question: 'What is Quill supposed to help my students with, and how is it meant to support what I already teach?',
    answer: (
      <p>Quill is built to provide additional, TEKS-aligned writing and grammar practice that supports the instruction already happening in your classroom. The activities offer short, targeted tasks that help with remediation, skill-building, and acceleration. Teachers can use them to strengthen foundational skills, give students extra exposure to key standards, or challenge students who are ready for more advanced work.</p>
    ),
    id: 'quill-support-what-i-teach'
  },
  {
    question: 'How does Quill fit with the ELA program I already use?',
    answer: (
      <p>Quill is meant to support your core instruction, not replace it. You can use the TEKS-aligned activities to reinforce the skills you&apos;re already teaching by plugging them into stations, intervention time, small groups, or independent practice. Since each activity aligns to a single TEKS standard, it&apos;s easy to match Quill assignments to the lessons and skills in your main curriculum.</p>
    ),
    id: 'quill-fit-with-ela-program'
  },
  {
    question: 'Does Quill have a clear scope and sequence so I know what skills are taught and when?',
    answer: (
      <p>Yes. Each grade level includes a TEKS-aligned, year-long sequence with one focused writing or grammar activity per week (about 38–39 total). Every activity aligns to a single TEKS ELAR standard, giving you a clear progression of skills across the year. The sequence is designed to build strong sentence-level writing and language skills through consistent, standards-aligned practice.</p>
    ),
    id: 'quill-scope-and-sequence'
  },
  {
    question: 'How do I assign a Quill activity?',
    answer: (
      <p>TEKS-aligned activity packs are only available to Quill Premium schools and districts.
        If you are a teacher at a Quill Premium school, you can assign a TEKS activity packs by going to the <a className='focus-on-light' href="/assign" rel="noopener noreferrer" target="_blank">Assign Activities tab</a> in your Dashboard and selecting the card for TEKS.
        Learn more about assigning activities <a className='focus-on-light' href="https://support.quill.org/en/collections/64484-assigning-and-managing-activities" rel="noopener noreferrer" target="_blank">here</a>.
        Learn more about Quill Premium and request a quote <a className='focus-on-light' href="/premium" rel="noopener noreferrer" target="_blank">here</a>.
      </p>
    ),
    id: 'assign-a-quill-activity'
  },
  {
    question: 'How much time do Quill activities take, and how often should I use them?',
    answer: (
      <p>Most activities take about 10 to 15 minutes and fit easily into warm-ups, stations, or independent practice. A good starting point is one activity per week to move through the 38–39 TEKS standards across the year. You can always slow down or speed up the pacing for intervention, small-group work, or extra practice.</p>
    ),
    id: 'quill-activity-time-and-usage'
  },
  {
    question: 'Does Quill offer resources and guidance to support educators in implementation?',
    answer: (
      <div>
        <p>Yes. Quill provides teachers with clear assignment workflows, standards alignment information, and step-by-step guidance through Teacher Center articles, instructional videos, and in-product tips. Every activity includes automated feedback for students and actionable data for teachers, helping them quickly identify skill gaps and determine who may need follow-up instruction.</p>
        <p>Quill&apos;s implementation support is grounded in research-based pedagogy. Our mastery-based learning guidance helps teachers use Diagnostics and practice-recommendation activity packs to create personalized learning paths, track progress, and encourage students to “go for green” through replay and revision. We also draw on evidence-based strategies, such as sentence combining—central to Quill Connect—which strengthens students&apos; syntactic maturity through targeted practice, immediate feedback, and multiple correct solution models.</p>
        <p>For educators seeking deeper support, Quill offers Professional Learning, onboarding for district partners, live and recorded webinars, and a growing library of Teacher Center articles to help teachers implement Quill effectively and confidently in any classroom context.</p>
      </div>
    ),
    id: 'quill-resources-and-guidance'
  },
  {
    question: 'What other Quill tools can help prepare my students for STAAR?',
    answer: (
      <p>In addition to our TEKS alignments, you can browse our extensive library of over 1,000 literacy activities. Search by concept to find the editing and revising skills you want to target.
        Quill Connect activities ask students to combine sentences, a specific skill tested on STAAR. In addition, our newest tool, Quill Reading for Evidence, offers great preparation for short-constructed response questions on STAAR.
        In Reading for Evidence, students in grades 8+ read cross-curricular texts and respond to open-ended prompts.
        Learn more about using Quill for STAAR readiness <a className='focus-on-light' href="https://www.quill.org/teacher-center/--pdf----quill-and-the-staar-test" rel="noopener noreferrer" target="_blank">here</a>.
      </p>
    ),
    id: 'tools-help-prepare-students-for-staar'
  }
]

export default teks
